import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  X,
  MapPin,
  Mail,
  Phone,
  CalendarCheck,
  Camera,
  UserPlus,
  Send,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatAdminError } from "@/lib/adminApi";
import {
  EmptyState,
  GhostButton,
  PageHeader,
  Panel,
  PrimaryButton,
  StatusBadge,
  adminCurrency,
  shortDate,
} from "@/components/admin/uiKit";
import ManualSignupDialog from "@/components/admin/ManualSignupDialog";
import PauseServicePanel from "@/components/admin/PauseServicePanel";

const PLAN_LABEL = {
  monthly: "Monthly",
  biweekly: "Bi-Weekly",
  onetime: "One-Time",
};

// R9 — frontend-only sort helpers. Intentionally does NOT import from the
// backend SERVICE_DAY_INDEX; ordering here is purely a UI presentation
// concern for the admin roster and must not couple to backend cadence
// logic. Sun → Mon → Tue → Wed → Thu → Fri → Sat.
const R9_SERVICE_DAY_ORDER = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const R9_PLAN_ORDER = {
  monthly: 0,
  biweekly: 1,
  onetime: 2,
};

// Sort keys the header row exposes.
const R9_SORT_KEYS = ["customer", "plan", "day", "status", "joined"];

// Extract a comparable value for a customer under a given sort key.
// Returns `null` when the field is missing so the comparator can push
// blanks to the end regardless of direction.
function r9SortValue(customer, key) {
  if (!customer) return null;
  if (key === "customer") {
    const raw = customer.full_name || customer.email || "";
    const trimmed = String(raw).trim();
    return trimmed ? trimmed.toLowerCase() : null;
  }
  if (key === "plan") {
    const code = customer.plan_code;
    if (code == null || code === "") return null;
    if (Object.prototype.hasOwnProperty.call(R9_PLAN_ORDER, code)) {
      return R9_PLAN_ORDER[code];
    }
    // Unknown plan codes sort after known ones but still before nulls.
    return 100 + String(code).toLowerCase().charCodeAt(0);
  }
  if (key === "day") {
    const day = customer.service_day;
    if (day == null || day === "") return null;
    if (Object.prototype.hasOwnProperty.call(R9_SERVICE_DAY_ORDER, day)) {
      return R9_SERVICE_DAY_ORDER[day];
    }
    return null;
  }
  if (key === "status") {
    const status = customer.subscription_status;
    if (status == null || status === "") return null;
    return String(status).toLowerCase();
  }
  if (key === "joined") {
    const t = customer.created_at;
    if (t == null || t === "") return null;
    const ts = Date.parse(t);
    if (Number.isNaN(ts)) return null;
    return ts;
  }
  return null;
}

// Deterministic comparator with stable-null-to-end behavior. Direction
// only flips the comparison of two non-null values; nulls always trail.
function r9CompareCustomers(a, b, key, dir) {
  const va = r9SortValue(a, key);
  const vb = r9SortValue(b, key);
  if (va == null && vb == null) return 0;
  if (va == null) return 1;
  if (vb == null) return -1;
  let cmp;
  if (typeof va === "number" && typeof vb === "number") {
    cmp = va - vb;
  } else {
    cmp = String(va).localeCompare(String(vb));
  }
  if (cmp === 0) return 0;
  return dir === "desc" ? -cmp : cmp;
}

export default function AdminCustomers() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  // R9 — frontend-only sort state. `sortBy === null` preserves the API
  // order the roster arrives in (backend is the source of truth for the
  // default listing); a header click activates client-side ordering.
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const load = () => {
    adminApi
      .get("/admin/customers", { params: q ? { q } : {} })
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => toast.error(formatAdminError(err)));
  };

  const totalActive = useMemo(
    () => (items || []).filter((c) => c.subscription_status === "active").length,
    [items],
  );

  // R9 — derived, sorted view of the roster. Never mutates `items`.
  const sortedItems = useMemo(() => {
    if (!items) return null;
    if (!sortBy) return items;
    if (!R9_SORT_KEYS.includes(sortBy)) return items;
    const copy = items.slice();
    copy.sort((a, b) => r9CompareCustomers(a, b, sortBy, sortDir));
    return copy;
  }, [items, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (!R9_SORT_KEYS.includes(key)) return;
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  return (
    <div data-testid="admin-customers">
      <PageHeader
        overline="Operations"
        title="Customers"
        description="Search, view, and tweak your roster. Plan changes regenerate upcoming jobs automatically."
        actions={
          <div className="flex items-center gap-2.5">
            <span className="text-xs text-slate-500 hidden sm:inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-100">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {totalActive} active
            </span>
            <PrimaryButton
              onClick={() => setManualOpen(true)}
              data-testid="admin-customers-add-button"
              className="inline-flex items-center gap-2"
            >
              <UserPlus size={14} /> Add Customer
            </PrimaryButton>
          </div>
        }
      />

      <div className="mb-5 flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, city, zip…"
            className="w-full h-11 pl-10 pr-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
            data-testid="admin-customers-search"
          />
        </div>
      </div>

      {sortedItems === null ? (
        <Panel className="p-10 text-center">
          <div className="h-6 w-6 mx-auto rounded-full border-2 border-sky-200 border-t-sky-500 animate-spin" />
        </Panel>
      ) : sortedItems.length === 0 ? (
        <EmptyState
          title="No customers match"
          description="Try a different search, or relax the filter."
        />
      ) : (
        <Panel testId="admin-customers-table" className="overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-slate-100 bg-slate-50 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
            <SortHeader
              className="col-span-3 lg:col-span-4"
              label="Customer"
              sortKey="customer"
              activeKey={sortBy}
              dir={sortDir}
              onToggle={toggleSort}
            />
            <SortHeader
              className="col-span-3"
              label="Plan"
              sortKey="plan"
              activeKey={sortBy}
              dir={sortDir}
              onToggle={toggleSort}
            />
            <SortHeader
              className="col-span-2"
              label="Service Day"
              sortKey="day"
              activeKey={sortBy}
              dir={sortDir}
              onToggle={toggleSort}
            />
            <SortHeader
              className="col-span-2"
              label="Status"
              sortKey="status"
              activeKey={sortBy}
              dir={sortDir}
              onToggle={toggleSort}
            />
            <SortHeader
              className="col-span-2 lg:col-span-1"
              label="Joined"
              sortKey="joined"
              activeKey={sortBy}
              dir={sortDir}
              onToggle={toggleSort}
              align="right"
            />
          </div>
          <ul className="divide-y divide-slate-100" data-testid="admin-customers-list">
            {sortedItems.map((c) => (
              <li
                key={c.id}
                className="px-4 sm:px-5 py-4 hover:bg-slate-50 transition-colors cursor-pointer"
                onClick={() => setSelectedId(c.id)}
                data-testid={`admin-customer-row-${c.id}`}
              >
                {/* Mobile card */}
                <div className="md:hidden">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="font-bold text-slate-900 truncate">
                          {c.full_name || c.email}
                        </p>
                        {c.source === "admin_manual" && <ManualPill />}
                      </div>
                      <p className="text-xs text-slate-500 truncate">{c.email}</p>
                    </div>
                    <StatusBadge status={c.subscription_status} />
                  </div>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-0.5">Plan</dt>
                      <dd className="text-slate-700">
                        {PLAN_LABEL[c.plan_code] || c.plan_code} · {c.bin_count} bin{c.bin_count === 1 ? "" : "s"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-0.5">Day</dt>
                      <dd className="text-slate-700">{c.service_day || "—"}</dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-0.5">Joined</dt>
                      <dd className="text-slate-700">{shortDate(c.created_at)}</dd>
                    </div>
                  </dl>
                  <button
                    type="button"
                    className="mt-4 w-full inline-flex items-center justify-center min-h-[44px] rounded-lg bg-slate-900 text-white text-sm font-bold tracking-wide"
                    data-testid={`admin-customer-open-${c.id}`}
                  >
                    Open profile
                  </button>
                </div>
                {/* Desktop row */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 md:items-center">
                  <div className="md:col-span-3 lg:col-span-4 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-bold text-slate-900 truncate">
                        {c.full_name || c.email}
                      </p>
                      {c.source === "admin_manual" && <ManualPill />}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{c.email}</p>
                  </div>
                  <div className="md:col-span-3 text-sm text-slate-700">
                    <span className="font-semibold">{PLAN_LABEL[c.plan_code] || c.plan_code}</span>
                    <span className="text-slate-400"> · </span>
                    <span className="text-slate-500">{c.bin_count} bin{c.bin_count === 1 ? "" : "s"}</span>
                  </div>
                  <div className="md:col-span-2 text-sm text-slate-600">{c.service_day || "—"}</div>
                  <div className="md:col-span-2">
                    <StatusBadge status={c.subscription_status} />
                  </div>
                  <div className="md:col-span-2 lg:col-span-1 md:text-right text-xs text-slate-500">
                    {shortDate(c.created_at)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {selectedId && (
        <CustomerDrawer
          customerId={selectedId}
          onClose={() => setSelectedId(null)}
          onUpdated={load}
        />
      )}

      <ManualSignupDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onCreated={load}
      />
    </div>
  );
}

function SortHeader({ className, label, sortKey, activeKey, dir, onToggle, align }) {
  const isActive = activeKey === sortKey;
  const ariaSort = isActive ? (dir === "desc" ? "descending" : "ascending") : "none";
  const isRight = align === "right";
  const Icon = isActive ? (dir === "desc" ? ArrowDown : ArrowUp) : ArrowUpDown;
  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        aria-sort={ariaSort}
        data-testid={`admin-customers-sort-${sortKey}`}
        data-active={isActive ? "true" : "false"}
        data-direction={isActive ? dir : "none"}
        className={
          "inline-flex items-center gap-1.5 uppercase tracking-[0.22em] text-[10px] font-bold text-slate-500 hover:text-slate-800 focus:outline-none focus:text-slate-900 " +
          (isActive ? "text-slate-900 " : "") +
          (isRight ? "justify-end w-full" : "")
        }
      >
        <span>{label}</span>
        <Icon
          size={12}
          aria-hidden="true"
          className={isActive ? "text-slate-900" : "text-slate-400"}
        />
      </button>
    </div>
  );
}

function ManualPill() {
  return (
    <span
      className="inline-flex items-center text-[9px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-md border border-violet-200 bg-violet-50 text-violet-700"
      data-testid="admin-customer-manual-pill"
    >
      Manual
    </span>
  );
}

function CustomerDrawer({ customerId, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .get(`/admin/customers/${customerId}`)
      .then(({ data: d }) => {
        if (cancelled) return;
        setData(d);
        setEditing({
          plan_code: d.customer.plan_code,
          bin_count: d.customer.bin_count,
          service_day: d.customer.service_day,
          subscription_status: d.customer.subscription_status,
        });
      })
      .catch((err) => toast.error(formatAdminError(err)));
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const { data: d } = await adminApi.patch(
        `/admin/customers/${customerId}`,
        editing,
      );
      setData((prev) => ({ ...prev, customer: d.customer }));
      toast.success("Customer updated");
      onUpdated?.();
    } catch (err) {
      toast.error(formatAdminError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      data-testid="admin-customer-drawer"
    >
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
      />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-white shadow-2xl overflow-y-auto">
        <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">
            Customer
          </p>
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 grid place-items-center"
            data-testid="admin-customer-drawer-close"
          >
            <X size={16} />
          </button>
        </header>

        {!data || !editing ? (
          <div className="p-10 text-center">
            <div className="h-6 w-6 mx-auto rounded-full border-2 border-sky-200 border-t-sky-500 animate-spin" />
          </div>
        ) : (
          <div className="p-6 space-y-7">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2
                  className="text-2xl font-extrabold tracking-tight text-slate-900"
                  style={{ fontFamily: "Outfit, ui-sans-serif" }}
                >
                  {data.customer.full_name}
                </h2>
                {data.customer.source === "admin_manual" && <ManualPill />}
              </div>
              <p className="mt-2 text-sm text-slate-500 flex items-center gap-2">
                <Mail size={12} /> {data.customer.email}
              </p>
              {data.customer.phone && (
                <p className="text-sm text-slate-500 flex items-center gap-2">
                  <Phone size={12} /> {data.customer.phone}
                </p>
              )}
              <p className="text-sm text-slate-500 flex items-center gap-2">
                <MapPin size={12} /> {data.customer.address_line1}, {data.customer.city}, {data.customer.state} {data.customer.zip}
              </p>
            </div>

            {data.customer.admin_notes && (
              <Panel className="p-4 bg-violet-50/40 border-violet-100">
                <p className="text-[10px] uppercase tracking-[0.22em] text-violet-700 font-bold mb-1.5">Internal notes</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.customer.admin_notes}</p>
              </Panel>
            )}

            <PauseServicePanel
              customer={data.customer}
              nextJob={
                (data.jobs || []).find(
                  (j) => j.status === "scheduled" && j.scheduled_for >= new Date().toISOString().slice(0, 10),
                )
              }
              onChanged={() => {
                // Re-fetch the customer detail so the banner + status flip
                onUpdated?.();
                adminApi
                  .get(`/admin/customers/${customerId}`)
                  .then(({ data: d }) => setData(d))
                  .catch(() => {});
              }}
            />

            {data.customer.subscription_status === "manual_pending_payment" && (
              <ManualPaymentLinkPanel customerId={customerId} />
            )}

            <Panel className="p-5">
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
                Plan
              </p>
              <div className="grid grid-cols-2 gap-3">
                <DrawerSelect
                  label="Plan"
                  value={editing.plan_code}
                  onChange={(v) => setEditing((e) => ({ ...e, plan_code: v }))}
                  options={[
                    ["monthly", "Monthly · $25 + $8/extra"],
                    ["biweekly", "Bi-Weekly · $40/mo + $8/extra bin"],
                    ["onetime", "One-Time · $40/bin"],
                  ]}
                  testId="admin-drawer-plan"
                />
                <DrawerSelect
                  label="Bins"
                  value={String(editing.bin_count)}
                  onChange={(v) =>
                    setEditing((e) => ({ ...e, bin_count: parseInt(v, 10) }))
                  }
                  options={[1, 2, 3, 4, 5, 6, 7, 8].map((n) => [
                    String(n),
                    `${n} bin${n === 1 ? "" : "s"}`,
                  ])}
                  testId="admin-drawer-bins"
                />
                <DrawerSelect
                  label="Service Day"
                  value={editing.service_day}
                  onChange={(v) => setEditing((e) => ({ ...e, service_day: v }))}
                  options={["Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => [d, d])}
                  testId="admin-drawer-day"
                />
                <DrawerSelect
                  label="Status"
                  value={editing.subscription_status}
                  onChange={(v) => setEditing((e) => ({ ...e, subscription_status: v }))}
                  options={["active", "pending", "past_due", "completed", "canceled"].map((s) => [s, s])}
                  testId="admin-drawer-status"
                />
              </div>
              <div className="mt-4 flex items-center gap-2">
                <PrimaryButton onClick={save} disabled={saving} data-testid="admin-drawer-save">
                  {saving ? "Saving…" : "Save changes"}
                </PrimaryButton>
                <GhostButton onClick={onClose}>Close</GhostButton>
              </div>
            </Panel>

            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
                Recent jobs ({data.jobs.length})
              </p>
              {data.jobs.length === 0 ? (
                <p className="text-sm text-slate-500">No jobs yet.</p>
              ) : (
                <ul className="space-y-2">
                  {data.jobs.slice(0, 6).map((j) => (
                    <li
                      key={j.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <CalendarCheck size={14} className="text-slate-400 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">
                            {shortDate(j.scheduled_for)}
                          </p>
                          <p className="text-xs text-slate-500">
                            {j.photos?.length || 0} photo{j.photos?.length === 1 ? "" : "s"}{" "}
                            <Camera size={10} className="inline" />
                          </p>
                        </div>
                      </div>
                      <StatusBadge status={j.status} />
                    </li>
                  ))}
                </ul>
              )}
              <Link
                to={`/admin/jobs?customer_id=${customerId}`}
                className="mt-3 inline-block text-sm font-bold text-sky-700 hover:text-sky-500"
                data-testid="admin-drawer-view-jobs"
              >
                View all jobs →
              </Link>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
                Recent payments ({data.payments.length})
              </p>
              {data.payments.length === 0 ? (
                <p className="text-sm text-slate-500">No payments recorded.</p>
              ) : (
                <ul className="space-y-2">
                  {data.payments.slice(0, 6).map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate">
                          {adminCurrency(p.amount_cents)}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {p.description || p.stripe_ref}
                        </p>
                      </div>
                      <StatusBadge status={p.status === "succeeded" ? "completed" : p.status} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

function ManualPaymentLinkPanel({ customerId }) {
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await adminApi.post(`/admin/customers/${customerId}/send-payment-link`);
      toast.success(`Payment link emailed to ${data.sent_to}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "session_not_open") {
        toast.error("Stripe session no longer open. Re-add the customer to regenerate.");
      } else {
        toast.error(formatAdminError(err));
      }
    } finally {
      setSending(false);
    }
  };
  return (
    <Panel className="p-4 border-amber-200 bg-amber-50/60">
      <p className="text-[10px] uppercase tracking-[0.22em] text-amber-800 font-bold mb-1.5">Awaiting payment</p>
      <p className="text-sm text-slate-700 mb-3">
        This customer hasn&rsquo;t completed checkout yet. Re-send the Stripe link by email.
      </p>
      <button
        type="button"
        onClick={send}
        disabled={sending}
        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-sky-500 text-white text-sm font-bold hover:bg-sky-600 disabled:opacity-60"
        data-testid="admin-drawer-resend-payment-link"
      >
        <Send size={14} /> {sending ? "Sending…" : "Re-send payment link"}
      </button>
    </Panel>
  );
}

function DrawerSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-1">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
        data-testid={testId}
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}
